import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { galleriSubpages } from "../data/siteData";

export default function Projects() {
  const [galleries, setGalleries] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxCaption, setLightboxCaption] = useState("");

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/galleries")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setGalleries(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching galleries:", err);
        setLoading(false);
      });
  }, []);

  // Consolidate images from scraped galleri subpages and CMS
  const allImages = useMemo(() => {
    const list = [];

    // First, add all images from scraped galleri subpages
    galleriSubpages.forEach((sub) => {
      sub.images.forEach((img) => {
        list.push({
          url: img.src,
          alt: img.alt || sub.heading,
          category: sub.heading,
          slug: sub.slug,
          type: "image"
        });
      });
    });

    // Also include any extra CMS gallery images if not duplicated
    galleries.forEach((g) => {
      const catName = g.title.da.replace(" - Galleri", "");
      if (Array.isArray(g.images)) {
        g.images.forEach((img) => {
          if (!list.some((existing) => existing.url === img)) {
            list.push({
              url: img,
              alt: catName,
              category: catName,
              slug: g.identifier,
              type: "image"
            });
          }
        });
      }
    });

    // Add videos
    list.push({
      url: "https://assets.auxodev.dk/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/img-5353.mp4",
      alt: "Grøn vedligeholdelse video",
      category: "Grøn vedligeholdelse",
      type: "video"
    });

    list.push({
      url: "https://assets.auxodev.dk/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/img-5334.mp4",
      alt: "Havearbejde video",
      category: "Grøn vedligeholdelse",
      type: "video"
    });

    return list;
  }, [galleries]);

  const categories = useMemo(() => {
    const cats = ["Alle", ...galleriSubpages.map((g) => g.heading)];
    return cats;
  }, []);

  const filteredImages = useMemo(() => {
    if (activeCategory === "Alle") {
      return allImages;
    }
    return allImages.filter((img) => img.category === activeCategory);
  }, [allImages, activeCategory]);

  const handlePrevImage = () => {
    if (!lightboxImage) return;
    const currentUrls = filteredImages.map((img) => img.url);
    const currentIndex = currentUrls.indexOf(lightboxImage);
    const prevIndex = (currentIndex - 1 + currentUrls.length) % currentUrls.length;
    setLightboxImage(currentUrls[prevIndex]);
    setLightboxCaption(filteredImages[prevIndex]?.alt || "");
  };

  const handleNextImage = () => {
    if (!lightboxImage) return;
    const currentUrls = filteredImages.map((img) => img.url);
    const currentIndex = currentUrls.indexOf(lightboxImage);
    const nextIndex = (currentIndex + 1) % currentUrls.length;
    setLightboxImage(currentUrls[nextIndex]);
    setLightboxCaption(filteredImages[nextIndex]?.alt || "");
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-brand-sand-50">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-light fa-spinner-third animate-spin text-4xl text-brand-green-700" />
          <p className="text-sm text-brand-charcoal/60 tracking-wider uppercase">Henter galleri...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[72px] bg-brand-sand-50 min-h-screen">
      <SEO
        title="Galleri | Elmegaarden Have og Anlæg"
        description="Se vores galleri med udført arbejde inden for terrasse, indkørsel, belægning, støttemur og haveanlæg i Hobro, Arden og Hadsund."
        canonical="/galleri"
      />

      {/* Header Banner */}
      <section className="bg-brand-green-950 text-white pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl text-left">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
              Galleri
            </h1>
            <p className="text-neutral-300 text-base md:text-lg leading-relaxed">
              Her kan du se billeder af et udpluk af vores projekter med belægning, støttemure, nye indkørsler, terrasser og haveanlæg.
            </p>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          {/* Subgalleries Link Boxes */}
          <div className="mb-14 text-left">
            <h2 className="font-display text-2xl font-bold text-brand-green-900 mb-6">
              Udforsk gallerier efter emne
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {galleriSubpages.map((sub) => (
                <Link
                  key={sub.slug}
                  to={`/galleri/${sub.slug}`}
                  className="bg-white border border-brand-sand-200/90 rounded-sm p-4 hover:border-brand-green-700 hover:shadow-sm transition-all group flex flex-col justify-between"
                >
                  <span className="font-bold text-sm text-brand-green-900 group-hover:text-brand-green-600 transition-colors">
                    {sub.heading}
                  </span>
                  <span className="text-xs text-brand-sand-600 mt-2 flex items-center gap-1 font-semibold">
                    {sub.images.length} billeder <i className="fa-solid fa-arrow-right text-[9px] group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap justify-start gap-2 mb-10 border-b border-brand-sand-200 pb-6">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-sm text-xs font-bold tracking-wider uppercase transition-all duration-300 cursor-pointer focus:outline-none ${
                  activeCategory === cat
                    ? "bg-[#7ca632] text-white shadow-sm"
                    : "bg-white text-brand-charcoal border border-brand-sand-200 hover:bg-brand-sand-100"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map((img, idx) => (
              <div
                key={idx}
                className="group bg-brand-sand-100 border border-brand-sand-200/60 rounded-sm overflow-hidden shadow-xs hover:shadow-md transition-all duration-300 flex flex-col"
              >
                <button
                  onClick={() => {
                    setLightboxImage(img.url);
                    setLightboxCaption(img.alt || img.category);
                  }}
                  className="relative aspect-4/3 overflow-hidden cursor-pointer focus:outline-none text-left w-full bg-black"
                >
                  {img.type === "video" ? (
                    <>
                      <video
                        src={`${img.url}#t=0.1`}
                        preload="metadata"
                        playsInline
                        muted
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 opacity-80"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-[#7ca632] text-white flex items-center justify-center text-lg shadow-lg transition-transform duration-300 group-hover:scale-110">
                          <i className="fa-solid fa-play ml-0.5" />
                        </div>
                      </div>
                      <span className="absolute bottom-3 left-3 bg-brand-green-900/90 text-white text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-sm">
                        Video
                      </span>
                    </>
                  ) : (
                    <>
                      <img
                        src={img.url.replace(
                          "https://assets.auxodev.dk/assets/",
                          "https://assets.auxodev.dk/cdn-cgi/image/width=600,height=450,fit=cover,quality=80,format=auto/assets/"
                        )}
                        alt={img.alt || `${img.category} billede`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-white text-brand-green-900 flex items-center justify-center shadow-md">
                          <i className="fa-solid fa-magnifying-glass-plus text-sm" />
                        </div>
                      </div>
                      <span className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-xs text-white text-[10px] font-semibold px-2 py-0.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                        {img.category}
                      </span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 transition-opacity duration-300"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-6 right-6 text-white hover:text-brand-sand-300 text-2xl p-2 cursor-pointer z-50"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {filteredImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevImage();
              }}
              className="absolute left-6 text-white hover:text-brand-sand-300 text-3xl p-4 cursor-pointer z-50"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
          )}

          {filteredImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              className="absolute right-6 text-white hover:text-brand-sand-300 text-3xl p-4 cursor-pointer z-50"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
          )}

          <div
            className="max-w-5xl max-h-[85vh] w-full flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxImage.endsWith(".mp4") ? (
              <video
                src={lightboxImage}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-[75vh] rounded-sm shadow-2xl"
              />
            ) : (
              <img
                src={lightboxImage.replace(
                  "https://assets.auxodev.dk/assets/",
                  "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/"
                )}
                alt={lightboxCaption || "Forstørret visning"}
                className="max-w-full max-h-[75vh] object-contain rounded-sm shadow-2xl"
              />
            )}
            {lightboxCaption && (
              <p className="text-white text-sm mt-3 font-medium text-center">
                {lightboxCaption}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
